const EXCLUDED_COLLECTION_HANDLES = [];

// Different collections can give different free products.
// Empty collectionHandles = storewide (all products except exclusions).
// Free Pumpkin Spice Pancakes offer DISABLED 2026-11-03 (replaced by 20% Off Menu with a side or drink).
// To bring it back, restore the offer object from git history (search-1103 repo).
const FREE_GIFT_OFFERS = [];

let cartWatcherRunning = false;
let rerunRequested = false;
let collectionProductIdsPromise = null;
let ensureFreeProductTimer = null;
let internalCartMutation = false;

const CART_ADD_URL = "/cart/add.js";
const CART_UPDATE_URL = "/cart/update.js";

function scheduleEnsureFreeProduct(delay = 500) {
  clearTimeout(ensureFreeProductTimer);
  ensureFreeProductTimer = setTimeout(() => {
    ensureFreeProduct();
  }, delay);
}

function toId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

// Gift variants from offers that have ended. They are still treated as gifts so a
// stale $0.00 line in a returning customer's cart is removed instead of shipping
// alongside the current gift.
const RETIRED_GIFT_VARIANTS = [
  50146458501370, // Buttermilk Pancake - $10 offer, retired 2026-10-14
  67729749901562, // Pumpkin Spice Pancakes - $20 offer, retired 2026-11-03
];

function getFreeVariants() {
  const active = FREE_GIFT_OFFERS.map((offer) => toId(offer.freeVariant));
  const retired = RETIRED_GIFT_VARIANTS.map(toId);
  return new Set([...active, ...retired].filter(Boolean));
}

function isGiftItem(item, freeVariants) {
  return freeVariants.has(toId(item.variant_id || item.id));
}

function isAddToCartTrigger(target) {
  if (!target) return false;
  if (target.closest('form[action*="/cart/add"]')) return true;
  if (target.closest('[name="add"]')) return true;
  if (target.closest("[data-add-to-cart], [data-atc]")) return true;
  if (target.closest(".product-form__submit, .m-add-to-cart")) return true;
  if (target.closest(".js-open-product-modal")) return true;
  return false;
}

function isRemoveTrigger(target) {
  if (!target) return false;
  if (target.closest("m-cart-remove-button")) return true;
  if (target.closest(".m-cart-item__remove")) return true;
  if (target.closest('[name="minus"]')) return true;
  return false;
}

async function postCartJson(url, payload) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
  }
  return data;
}

async function getCart() {
  const response = await fetch(`/cart.js?t=${Date.now()}`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  return response.json();
}

async function getProductsByCollectionHandle(collectionHandle) {
  const ids = new Set();
  const limit = 30;
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await fetch(
      `/collections/${collectionHandle}/products.json?limit=${limit}&page=${page}`
    );
    if (!response.ok) break;

    const data = await response.json();
    const products = data.products || [];
    products.forEach((product) => {
      const productId = toId(product.id);
      if (productId) ids.add(productId);
    });

    hasMore = products.length === limit;
    page += 1;
  }

  return ids;
}

async function getCollectionProductIdsMap() {
  if (collectionProductIdsPromise) return collectionProductIdsPromise;

  collectionProductIdsPromise = (async () => {
    const handles = new Set();
    EXCLUDED_COLLECTION_HANDLES.forEach((handle) => handle && handles.add(handle));
    FREE_GIFT_OFFERS.forEach((offer) => {
      (offer.collectionHandles || []).forEach((handle) => handle && handles.add(handle));
    });

    const collectionMap = new Map();
    await Promise.all(
      [...handles].map(async (handle) => {
        try {
          collectionMap.set(handle, await getProductsByCollectionHandle(handle));
        } catch (error) {
          collectionMap.set(handle, new Set());
        }
      })
    );
    return collectionMap;
  })();

  return collectionProductIdsPromise;
}

function inCollections(productId, handles, collectionMap) {
  const id = toId(productId);
  return handles.some((handle) => collectionMap.get(handle)?.has(id));
}

function normalizeQuantity(quantity) {
  const parsed = Math.floor(Number(quantity));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function getTargetQuantity(offer, subtotalCents, hasEligibleItems) {
  if (!hasEligibleItems) return 0;

  const subtotal = subtotalCents / 100;
  const minCartValue = Number(offer.minCartValue) || 0;
  if (subtotal >= minCartValue) return normalizeQuantity(offer.quantity);
  return 0;
}

function getEligibleItems(offer, cartItems, collectionMap, freeVariants) {
  const excludedHandles = EXCLUDED_COLLECTION_HANDLES.filter(Boolean);
  const offerHandles = (offer.collectionHandles || []).filter(Boolean);

  return cartItems.filter((item) => {
    if (isGiftItem(item, freeVariants)) return false;
    if (item.sku === "CATERING-DELIVERY-FEE") return false; // the $30 catering delivery fee never earns a gift
    if (inCollections(item.product_id, excludedHandles, collectionMap)) return false;
    if (!offerHandles.length) return true;
    return inCollections(item.product_id, offerHandles, collectionMap);
  });
}

function refreshCartUI(cart) {
  const drawer = document.querySelector("m-cart-drawer");
  if (drawer && typeof drawer.onCartDrawerUpdate === "function") {
    drawer.onCartDrawerUpdate();
  }

  document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: cart }));
  document.dispatchEvent(
    new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: { cart, open: true },
    })
  );
}

async function ensureFreeProduct() {
  if (cartWatcherRunning) {
    rerunRequested = true;
    return;
  }
  cartWatcherRunning = true;

  try {
    const [collectionMap, cart] = await Promise.all([
      getCollectionProductIdsMap(),
      getCart(),
    ]);

    const freeVariants = getFreeVariants();
    const cartItems = cart.items || [];
    const paidItems = cartItems.filter((item) => !isGiftItem(item, freeVariants));
    const desiredByVariant = new Map();

    FREE_GIFT_OFFERS.forEach((offer) => {
      const variantId = toId(offer.freeVariant);
      if (!variantId) return;

      if (!paidItems.length) {
        desiredByVariant.set(variantId, 0);
        return;
      }

      const eligibleItems = getEligibleItems(offer, cartItems, collectionMap, freeVariants);
      const subtotalCents = eligibleItems.reduce(
        (sum, item) => sum + Number(item.final_line_price || item.line_price || 0),
        0
      );
      desiredByVariant.set(
        variantId,
        getTargetQuantity(offer, subtotalCents, eligibleItems.length > 0)
      );
    });

    const itemsToAdd = [];
    const updates = {};
    const handledKeys = new Set();

    desiredByVariant.forEach((quantity, variantId) => {
      const giftLine = cartItems.find((item) => toId(item.variant_id || item.id) === variantId);

      if (quantity > 0 && !giftLine) {
        itemsToAdd.push({ id: variantId, quantity });
        return;
      }

      if (giftLine) {
        handledKeys.add(giftLine.key);
        if (Number(giftLine.quantity) !== quantity) {
          updates[giftLine.key] = quantity;
        }
      }
    });

    cartItems.forEach((item) => {
      if (handledKeys.has(item.key) || !isGiftItem(item, freeVariants)) return;
      updates[item.key] = 0;
    });

    const hasUpdates = Object.keys(updates).length > 0;
    const hasAdds = itemsToAdd.length > 0;
    if (!hasUpdates && !hasAdds) return;

    internalCartMutation = true;
    try {
      if (hasUpdates) await postCartJson(CART_UPDATE_URL, { updates });
      if (hasAdds) await postCartJson(CART_ADD_URL, { items: itemsToAdd });
    } finally {
      internalCartMutation = false;
    }

    refreshCartUI(await getCart());
  } catch (error) {
    console.error("Free gift cart watcher error:", error);
  } finally {
    cartWatcherRunning = false;
    if (rerunRequested) {
      rerunRequested = false;
      scheduleEnsureFreeProduct(250);
    }
  }
}

function onCartChanged() {
  if (internalCartMutation) return;
  scheduleEnsureFreeProduct(500);
}

window.ensureFreeProduct = ensureFreeProduct;
window.scheduleEnsureFreeProduct = scheduleEnsureFreeProduct;

document.addEventListener("cart:refresh", onCartChanged);
document.addEventListener("cart:updated", onCartChanged);
document.addEventListener("minimog:cart-external-updated", onCartChanged);
document.addEventListener("DOMContentLoaded", ensureFreeProduct);
document.addEventListener("free-gift:check", () => scheduleEnsureFreeProduct(300));

if (
  window.MinimogEvents &&
  typeof window.MinimogEvents.subscribe === "function" &&
  window.MinimogTheme?.pubSubEvents?.cartUpdate
) {
  window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, onCartChanged);
}

document.addEventListener(
  "click",
  (event) => {
    if (isAddToCartTrigger(event.target) || isRemoveTrigger(event.target)) {
      scheduleEnsureFreeProduct(800);
    }
  },
  true
);

document.addEventListener(
  "change",
  (event) => {
    if (event.target?.matches?.(".m-quantity__input, [name='updates[]']")) {
      scheduleEnsureFreeProduct(800);
    }
  },
  true
);

document.addEventListener(
  "submit",
  (event) => {
    if (event.target?.matches?.('form[action*="/cart/add"]') || event.target?.querySelector?.("[name='add']")) {
      scheduleEnsureFreeProduct(800);
    }
  },
  true
);
